import { FC, useState, useCallback } from 'react';
import Page, { PageProps } from '../../../components/page';
import DataTable from '../../../components/data-table';
import { Box, Stack } from '@mui/material';
import Text from '../../../components/text';
import PageHeader from '../../../components/page/header';
import { DtColumn, DtFilter, DtPage } from '@/components/data-table/helpers';
import { getGroups } from '@/lib/services/group.service';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import Button from '@/components/button';
import Chip from '@/components/chip';
import { QueryKey } from '@/lib/query-client';
import DomSize from '@/components/dom-size';
import useFeature from '@/hooks/use-feature.hook';
import Tooltip from '@/components/tooltip';
import PageFeatureToast from '@/components/page-feature-toast';
import { getNiceNumber } from '@/lib/helpers';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';
import { getPoliciesByGroupIdsV1 } from '@/lib/services/policy-v1.service';
import NoneChip from '@/components/chip/none-chip';
import useDrawer from '@/hooks/use-drawer.hook';
import { PolicyMeta } from '@/lib/models/policy-model-v1';

const columns: DtColumn[] = [
  { name: 'name', label: 'Name', flex: 1.5 },
  { name: 'description', label: 'Description', flex: 2, sortable: false },
  { name: 'userCount', label: 'Users', flex: 0.5, sortable: false },
  { name: 'policies', label: 'Group Policies', flex: 2, sortable: false },
  { name: 'action', label: '', sortable: false },
];

const GroupsPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const [selectedRowId, setSelectedRowId] = useState('');
  const { canChangeGroups, getTooltip } = useFeature();

  const handleView = useCallback(
    (groupId?: string) => {
      openDrawer('group', {
        id: groupId || null,
        onClose: () => {
          setSelectedRowId('');
        },
      });

      setSelectedRowId(groupId || '');
    },
    [openDrawer]
  );

  const loadData = useCallback(
    async (pageParams: DtPage, filter?: DtFilter) => {
      const { page, pageSize, sort } = pageParams;
      const dataset = await getGroups(page, pageSize, sort, filter);
      const groups = dataset.rows;
      const groupIds = groups.map((group) => group.id);
      const allGroupPolicies = await getPoliciesByGroupIdsV1(groupIds);

      return {
        ...dataset,
        rows: groups.map(
          ({ id, name, description, hasDuplicateName, userCount, policies: groupPolicyIds }) => {
            const hasPolicies = !!groupPolicyIds.length;
            const groupPolicies = hasPolicies
              ? allGroupPolicies.filter((policy) => groupPolicyIds.includes(policy.id))
              : [];

            return {
              id,
              name: (
                <Stack>
                  <Box>{name}</Box>
                  {hasDuplicateName && (
                    <Box mr={2}>
                      <Chip label="Duplicate Name" size="x-small" color="warning" />
                    </Box>
                  )}
                </Stack>
              ),
              description: (
                <Text color="grey" size="small" dotdot>
                  {description}
                </Text>
              ),
              userCount: <Box width="100%">{getNiceNumber(userCount)}</Box>,
              policies: (
                <Box maxWidth="100%">
                  <Text dotdot noWrap size="small">
                    {hasPolicies ? (
                      <Stack direction="row" gap={1}>
                        {groupPolicies.map(({ id, name }: PolicyMeta) => (
                          <Chip key={id} label={name} size="small" />
                        ))}
                      </Stack>
                    ) : (
                      <NoneChip />
                    )}
                  </Text>
                </Box>
              ),
              action: <OpenDrawerButton onClick={() => handleView(id)} />,
            };
          }
        ),
      };
    },
    [handleView]
  );

  return (
    <Page title="Groups">
      <PageHeader>
        <PageBlurb>Apply intent, data, and model access policies to groups of users.</PageBlurb>
        <PageTools>
          <Tooltip title={getTooltip('add-group')} disabled={canChangeGroups}>
            <Button
              label="Add Group"
              icon="plus"
              size="small"
              onClick={() => handleView()}
              disabled={!canChangeGroups}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <PageFeatureToast featureId="add-group" can={canChangeGroups} />
      <DomSize>
        <DataTable
          queryKey={QueryKey.GroupsDataset}
          searchPlaceholder="Search by group name"
          columns={columns}
          onLoad={loadData}
          rowHeight={70}
          search
          selectedRowId={selectedRowId}
          sort={{ columnName: 'name', direction: 'asc' }}
        />
      </DomSize>
    </Page>
  );
};

export default GroupsPage;
